import * as React from 'react'
import {
  b,
  constFalse,
  constVoid,
  DOM,
  E,
  flow,
  identity,
  IO,
  IOO,
  O,
  pipe,
  RA,
  RD,
  RR,
  s,
  S,
  t,
  T,
  TE,
  TO,
  URLFromString
} from '@packages/prelude'
import { css, styled } from 'styled-components'
import { Accordion as BSAccordion, Col, Container as BSContainer, Row } from 'react-bootstrap'
import { type AccordionEventKey } from 'react-bootstrap/esm/AccordionContext'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faCommentText,
  faDownload,
  faRocket,
  faRotateRight,
  faShoppingCart,
  faStar,
  faVideo
} from '@fortawesome/pro-solid-svg-icons'
import * as domain from '@packages/domain'
import { fromTraversable, Lens, Prism } from 'monocle-ts'
import { indexReadonlyRecord } from 'monocle-ts/Index/ReadonlyRecord'
import { formatValidationErrors } from 'io-ts-reporters'
import ImageGallery from 'react-image-gallery'
import ReactPlayer from 'react-player'
import { useIndiAudioPreview, PreviewData } from '~/hooks/useIndiAudioPreview'
import {
  AudioPlayer,
  AudioRecorder,
  ShareWidget,
  ProposalSwiper,
  Loading,
  ModalHint,
  NameSelector,
  type NameItem,
  StarGreeting
} from '~/components'
import { AccordionToggleButton, ControlButton } from '~/components/Buttons'
import { react } from '~/commons'

/**
 * Components
 */

const Container = styled(BSContainer)`
  position: relative;
  min-height: 400px;
`

const Title = styled.h2`
  margin-top: 6rem;
`

const Description = styled.div`
  font-size: 1.25rem;
  margin-bottom: 1rem;
  ul {
    margin-left: 1rem;
  }
`

const Image = styled.img`
  width: 100%;
`

const Wrapper = styled.div`
  position: relative;
  border-radius: 3rem;
  overflow: hidden;
`

const Overlay = styled.div`
  position: absolute;
  inset: 0;
  background: var(--primary);
  mix-blend-mode: multiply;
`

const OrderButton = styled.button.attrs({
  className: 'btn btn-lg text-white text-uppercase fw-bold px-4 py-3 mt-5 align-self-stretch'
})<{ size?: 'lg' }>`
  ${props =>
    props.size === 'lg' &&
    css`
      font-size: 2rem !important;
    `}
`

const PreviewButton = styled.button.attrs({
  className: 'btn btn-lg text-white text-uppercase fw-bold px-4 py-3 mt-5 align-self-stretch'
})<{ size?: 'lg'; className?: string }>`
  ${props =>
    props.size === 'lg' &&
    css`
      @media (min-width: 992px) {
        font-size: 2rem !important;
      }
    `}
`

const DownloadButton = styled.a.attrs({
  className: 'btn btn-lg text-white text-uppercase fw-bold px-4 py-3 mt-3'
})``

const ResetButton = styled(ControlButton).attrs({
  className: 'btn text-white text-uppercase fw-bold p-3 mt-3'
})`
  background-color: var(--button-color);
  position: absolute;
  right: -1rem;
  top: -2rem;
`

const TextContainer = styled.div`
  flex: 1;
  min-width: 200px;
  font-size: clamp(1rem, 4vw, 1.5rem);
  margin-right: auto;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.25rem;
`

const ButtonContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-left: auto;

  @container (max-width: 450px) {
    width: 100%;
    justify-content: center;
    margin-top: 0.5rem;
  }
`

const AudioRecorderWrapper = styled.div`
  container-type: inline-size;
  width: 100%;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  background: var(--primary);
  color: var(--bs-white);
  border-radius: 2rem;

  .btn {
    box-shadow: none !important;
    background: var(--bs-white) !important;
    color: var(--primary) !important;
    margin: 0 !important;
    padding: 0 !important;
    width: 3rem !important;
    height: 3rem !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    flex-shrink: 0 !important;
    border-radius: 50% !important;
    border-color: var(--primary);
  }
`

const Accordion = styled(BSAccordion)`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 1rem;
`

const AccordionCollapse = styled(BSAccordion.Collapse)`
  /* center video */
  &:has(video) {
    margin: 0 auto;
  }
`

/**
 * Data types
 */

interface AudioData {
  audio: O.Option<Blob>
  url: O.Option<string>
  remoteUrl: O.Option<string>
  id: number
  maxDuration: number
  title: string
  description: string
  optional: boolean
  fieldId: string
  video: O.Option<URL>
}

interface NameData {
  name: O.Option<string>
  id: number
  title: string
  description: string
  optional: boolean
  fieldId: string
}

interface GreetingData {
  title: string
  description: string
  id: number
  optional: boolean
  fieldId: string
  greeting: O.Option<string>
  selected: boolean
}

/**
 * Lenses
 */

const audioDataById = (id: string) => indexReadonlyRecord<AudioData>().index(id)
const selectedNameDataById = (id: string) => indexReadonlyRecord<NameData>().index(id)

const selectedNameDataIdByFieldId = (fieldId: string) => {
  const nameDataIdFold = fromTraversable(RR.getTraversable(s.Ord))<NameData>()
    .filter(name => name.fieldId === fieldId)
    .composeLens(Lens.fromProp<NameData>()('id'))
    .asFold()

  return nameDataIdFold.headOption.bind(nameDataIdFold)
}

const greetingDataById = (id: string) => indexReadonlyRecord<GreetingData>().index(id)

const audioLens = (id: string) => audioDataById(id).composeLens(Lens.fromProp<AudioData>()('audio'))
const urlLens = (id: string) => audioDataById(id).composeLens(Lens.fromProp<AudioData>()('url'))

const nameLens = (id: string) => selectedNameDataById(id).composeLens(Lens.fromProp<NameData>()('name'))

const starGreetingsGreetingLens = (id: string) => greetingDataById(id).composeLens(Lens.fromProp<GreetingData>()('greeting'))
const starGreetingsSelectedLens = (id: string) => greetingDataById(id).composeLens(Lens.fromProp<GreetingData>()('selected'))

const AudioDataTraversable = fromTraversable(RR.getTraversable(s.Ord))<AudioData>()

const ChoicesLens = domain.Meta.Fields.Format.Lens.options.composeLens(
  Lens.fromProp<domain.Meta.Fields.Format.Type['options']>()('choices')
)

const ChoicesTraversable = fromTraversable(RA.Traversable)<domain.Meta.Fields.Choice.Type>()

const getAllAudios = AudioDataTraversable.composeLens(Lens.fromProps<AudioData>()(['audio', 'id'])).asFold().getAll

const getAllUrls = AudioDataTraversable.composeLens(Lens.fromProp<AudioData>()('url')).composePrism(Prism.some()).asFold().getAll

const hasRequired = AudioDataTraversable.composeLens(Lens.fromProp<AudioData>()('optional')).asFold().exist(b.not)

const hasAllRequiredAudio = AudioDataTraversable.composeLens(Lens.fromProps<AudioData>()(['audio', 'optional']))
  .filter(({ optional }) => !optional)
  .asFold()
  .all(({ audio }) => O.isSome(audio))

// TODO: Enable, when the backend supports it
// // get all audio data, for which a recording exists but which has not been uploaded yet
// const getAllAudiosForUpload = AudioDataTraversable.composePrism(
//   Prism.fromPredicate(audioData => O.isSome(audioData.audio) && O.isNone(audioData.remoteUrl))
// ).asFold().getAll

/**
 * Props
 */

interface Props extends domain.Product.Type {
  title?: string
  config: domain.Api.Config.Type
  initialData: E.Either<domain.Error.Type, domain.Api.IALoadPreviewResponse.Type>
}

/**
 * View
 */
export const View: React.FC<Props> = ({
  config,
  image,
  gallery,
  title,
  description,
  parameters,
  productId,
  meta,
  name,
  initialData: loadedInitialData
}) => {
  const [initialData, setInitialData] =
    React.useState<E.Either<domain.Error.Type, domain.Api.IALoadPreviewResponse.Type>>(loadedInitialData)
  const [audioData, setAudioData] = React.useState<RR.ReadonlyRecord<string, AudioData>>(RR.empty)
  const [previewData, setPreviewData] = React.useState<RD.RemoteData<domain.Error.Type, domain.Api.IAGenPreviewResponse.Type>>(
    pipe(
      initialData,
      E.fold(() => RD.initial, RD.success)
    )
  )

  const [isOrderable, setIsOrderable] = React.useState(false)
  const [currentRecorder, setCurrentRecorder] = React.useState<O.Option<string>>(O.none)
  const { generatePreview } = useIndiAudioPreview(productId, config)
  const [options, setOptions] = React.useState<RD.RemoteData<Error, domain.Api.Options.Type>>(RD.initial)
  const [formData, setFormData] = React.useState<FormData>(new FormData())
  const [availableNames, setAvailableNames] = React.useState<RD.RemoteData<Error, ReadonlyArray<NameItem>>>(RD.initial)
  const [selectedNames, setSelectedNames] = React.useState<RR.ReadonlyRecord<string, NameData>>(RR.empty)
  const [starGreeting, setStarGreeting] = React.useState<RR.ReadonlyRecord<string, GreetingData>>(RR.empty)
  const videoPlayerRef = React.useRef<ReactPlayer>(null)
  const [showModalHint, setShowModalHint] = React.useState(false)

  const initializeAudioData = React.useCallback(() => {
    pipe(
      RA.zipWith(
        pipe(meta.fields, RA.filter(domain.Meta.Fields.Field.ADT.is['pers-widmung-audio'])),
        pipe(parameters, RA.filter(domain.IAParameter.ADT.is.UO)),
        (field, param) =>
          pipe(
            TO.fromEither(initialData),
            TO.bind('remoteUrl', d =>
              TO.fromOption(domain.Api.IALoadPreviewResponse.fileById(String(param.id)).asFold().headOption(d))
            ),
            TO.bind('audio', ({ remoteUrl }) => TO.tryCatch(() => fetch(remoteUrl).then(data => data.blob()))),
            TO.bind('url', ({ audio }) => TO.of(URL.createObjectURL(audio))),
            TO.match(
              () => ({
                audio: O.none,
                url: O.none,
                remoteUrl: O.none
              }),
              ({ audio, url, remoteUrl }) => ({
                audio: O.some(audio),
                url: O.some(url),
                remoteUrl: O.some(remoteUrl)
              })
            ),
            T.map(({ audio, url, remoteUrl }) => ({
              id: param.id,
              audio,
              url,
              remoteUrl,
              maxDuration: param['max-duration'],
              title: param.name,
              description: param.description,
              optional: param.optional,
              fieldId: field.id,
              video: O.fromEither(URLFromString.decode(field.description))
            }))
          )
      ),
      T.sequenceArray,
      T.map(params => RR.fromFoldableMap(S.last<AudioData>(), RA.Foldable)(params, param => [String(param.id), param])),
      T.map(setAudioData)
    )()
  }, [meta.fields, parameters, initialData])

  const initializeFormData = React.useCallback(() => {
    pipe(
      IO.of(new FormData()),
      IO.chainFirst(fd => () => {
        fd.append('add-to-cart', String(productId))
        fd.append('quantity', '1')
        fd.append('wapf_field_groups', `p_${productId}`)

        // set delivery format choice (mp3, CD) to the default choice
        pipe(
          O.Do,
          O.apS('field', pipe(meta.fields, RA.findFirst(domain.Meta.Fields.Format.Type.is))),
          O.bind('choice', ({ field }) =>
            // this additional arrow function is needed due to a bug in monocle-ts described here:
            // https://github.com/gcanti/monocle-ts/issues/96
            pipe(field, f =>
              ChoicesLens.composeTraversal(ChoicesTraversable)
                .composePrism(Prism.fromPredicate(choice => choice.selected))
                .asFold()
                .headOption(f)
            )
          ),
          O.map(({ field, choice }) => fd.append(`wapf[field_${field.id}]`, choice.slug))
        )
      }),
      // add recordings to form data
      IO.chainFirst(fd =>
        pipe(
          IO.of(audioData),
          IO.map(
            RR.map(data =>
              pipe(
                data.remoteUrl,
                O.match(constVoid, url => fd.append(`wapf[field_${data.fieldId}]`, url))
              )
            )
          )
        )
      ),
      // add names to form data
      IO.chainFirst(fd =>
        pipe(
          IO.of(selectedNames),
          IO.map(
            RR.map(data =>
              pipe(
                data.name,
                O.match(constVoid, name => fd.append(`wapf[field_${data.fieldId}]`, name))
              )
            )
          )
        )
      ),
      // add star greetings to form data
      IO.chainFirst(fd =>
        pipe(
          IO.of(starGreeting),
          IO.map(
            RR.map(data =>
              pipe(
                data.greeting,
                O.match(constVoid, greeting => fd.append(`wapf[field_${data.fieldId}]`, greeting))
              )
            )
          )
        )
      ),
      IO.map(setFormData)
    )()
  }, [productId, meta.fields, audioData, selectedNames, starGreeting])

  const initializeSelectedNames = React.useCallback(() => {
    pipe(
      RA.zipWith(
        pipe(meta.fields, RA.filter(domain.Meta.Fields.Field.ADT.isAnyOf(['pers-name', 'pers-name-2']))),
        pipe(parameters, RA.filter(domain.IAParameter.ADT.is.PE)),
        (field, param) => ({
          id: param.id,
          name: pipe(
            initialData,
            O.fromEither,
            O.chain(domain.Api.IALoadPreviewResponse.parameterById(String(param.id)).getOption)
          ),
          title: field.label,
          description: param.description,
          optional: param.optional,
          fieldId: field.id
        })
      ),
      params => RR.fromFoldableMap(S.last<NameData>(), RA.Foldable)(params, param => [String(param.id), param]),
      setSelectedNames
    )
  }, [initialData, meta, parameters])

  const initializeGreetings = React.useCallback(() => {
    pipe(
      RA.zipWith(
        pipe(meta.fields, RA.filter(domain.Meta.Fields.Field.ADT.is['pers-sprecherwidmung'])),
        pipe(parameters, RA.filter(domain.IAParameter.ADT.is.VU)),
        (field, param) => ({
          id: param.id,
          greeting: pipe(
            initialData,
            O.fromEither,
            O.chain(domain.Api.IALoadPreviewResponse.parameterById(String(param.id)).getOption)
          ),
          title: field.label,
          description: param.description,
          optional: param.optional,
          fieldId: field.id,
          selected: pipe(
            initialData,
            O.fromEither,
            O.chain(domain.Api.IALoadPreviewResponse.parameterById(String(param.id)).getOption),
            O.isSome
          )
        })
      ),
      params => RR.fromFoldableMap(S.last<GreetingData>(), RA.Foldable)(params, param => [String(param.id), param]),
      setStarGreeting
    )
  }, [meta, parameters, initialData])

  // scroll to product widget on initial load, if state was restored from transient (e.g. when sharing a widget)
  React.useEffect(() => {
    pipe(
      IOO.fromEither(initialData),
      IOO.chain(({ product }) => pipe(document, DOM.querySelector(`#product-widget-${product}`))),
      IOO.chainIOK(widget => () => widget.scrollIntoView({ behavior: 'smooth' }))
    )()
  }, [initialData])

  // show modal hint if audio was not found
  React.useEffect(() => {
    pipe(
      initialData,
      E.fold(
        domain.Error.Type.match(
          {
            AudioNotFoundError: () => setShowModalHint(true)
          },
          _ => setShowModalHint(false)
        ),
        () => setShowModalHint(false)
      )
    )
  }, [initialData])

  React.useEffect(() => {
    initializeAudioData()
  }, [initializeAudioData])

  React.useEffect(() => {
    initializeFormData()
  }, [initializeFormData])

  React.useEffect(() => {
    initializeSelectedNames()
  }, [initializeSelectedNames])

  React.useEffect(() => {
    initializeGreetings()
  }, [initializeGreetings])

  React.useEffect(() => {
    setIsOrderable(hasRequired(audioData) ? hasAllRequiredAudio(audioData) : true)
  }, [audioData])

  React.useEffect(() => {
    pipe(
      TE.tryCatch(() => fetch(`${config.wpApiBase}/options/all`).then(response => response.json()), E.toError),
      TE.chainEitherK(({ greeting_proposals, button_label }) =>
        pipe(
          domain.Api.Options.Type.decode({
            proposals: greeting_proposals,
            buttonLabel: button_label
          }),
          E.mapLeft(flow(formatValidationErrors, RA.join(', '), E.toError))
        )
      ),
      TE.match(
        error => setOptions(RD.failure(error)),
        options => setOptions(RD.success(options))
      )
    )()
  }, [config])

  React.useEffect(() => {
    pipe(
      TE.tryCatch(() => fetch(`${config.wpApiBase}/indiaudio/names/${productId}`).then(response => response.json()), E.toError),
      TE.chainEitherK(({ names }) =>
        pipe(t.readonlyArray(t.string).decode(names), E.mapLeft(flow(formatValidationErrors, RA.join(', '), E.toError)))
      ),
      TE.match(
        error => setAvailableNames(RD.failure(error)),
        names =>
          pipe(
            names,
            RA.mapWithIndex((id, name) => ({ id, name })),
            RD.success,
            setAvailableNames
          )
      )
    )()
  }, [config, productId])

  React.useEffect(() => {
    pipe(
      previewData,
      RD.match3W(
        () => constVoid,
        () => constVoid,
        data =>
          pipe(
            data.files,
            RA.map(
              RR.mapWithIndex((id, file) =>
                setAudioData(
                  audioDataById(id).modify(audioData => ({
                    ...audioData,
                    remoteUrl: O.some(file)
                  }))
                )
              )
            )
          )
      )
    )
  }, [previewData])

  /*
    Callback functions
   */

  const onPreviewClick = React.useCallback(
    () =>
      pipe(
        TE.fromIO(() => setPreviewData(RD.pending)),
        TE.chain(() =>
          pipe(
            audioData,
            getAllAudios,
            RA.filterMap(({ audio, id }) =>
              pipe(
                audio,
                O.map(audio => PreviewData.of.Recording({ id, audio }))
              )
            ),
            RA.concat(
              pipe(
                selectedNames,
                RR.values,
                RA.filterMap(({ id, name }) =>
                  pipe(
                    name,
                    O.map(name => PreviewData.of.Name({ id, name }))
                  )
                )
              )
            ),
            RA.concat(
              pipe(
                starGreeting,
                RR.values,
                RA.filterMap(({ id, greeting, selected }) =>
                  pipe(
                    selected,
                    O.fromPredicate(identity),
                    O.chain(() => greeting),
                    O.map(greeting => PreviewData.of.StarGreeting({ id, greeting }))
                  )
                )
              )
            ),
            generatePreview
          )
        ),
        TE.map(response => setPreviewData(RD.success(response))),
        TE.mapLeft(e => setPreviewData(RD.failure(domain.Error.toUnknownError(e))))
      ),
    [audioData, generatePreview, selectedNames, starGreeting]
  )

  const onRecordingComplete = (id: string) => (blob: Blob) =>
    setAudioData(
      flow(
        audioLens(id).set(O.some(blob)),
        urlLens(id).modify(url => {
          // reset audio reference to allow garbage collection of old data
          pipe(url, O.map(URL.revokeObjectURL))

          return O.some(URL.createObjectURL(blob))
        })
      )
    )

  const onIsRecording = React.useCallback(
    (key: string) => (isRecording: boolean) => {
      pipe(
        isRecording,
        b.match(
          () =>
            pipe(
              O.getEq(s.Eq).equals(currentRecorder, O.some(key)),
              b.match(constVoid, () => setCurrentRecorder(O.none))
            ),
          () => setCurrentRecorder(O.some(key))
        )
      )
    },
    [currentRecorder]
  )

  const onDiscard = (id: string) =>
    setAudioData(
      flow(
        audioLens(id).set(O.none),
        urlLens(id).modify(url => {
          // reset audio reference to allow garbage collection of old data
          pipe(url, O.map(URL.revokeObjectURL))

          return O.none
        })
      )
    )

  const onResetClick = React.useCallback(() => {
    // cleanup audio data references
    pipe(audioData, getAllUrls, RA.map(URL.revokeObjectURL))

    // reset to initial states
    setInitialData(E.left(domain.Error.fromString('reset')))
    setAudioData(RR.empty)
    setSelectedNames(RR.empty)
    setStarGreeting(RR.empty)
    setPreviewData(RD.initial)
    setCurrentRecorder(O.none)

    // remove possible hash from URL
    history.replaceState({}, document.title, window.location.pathname)
  }, [audioData])

  const onOrderClick = React.useCallback(() => {
    // pipe(
    //   audioData,
    //   getAllAudiosForUpload,
    //   RA.reduce(new FormData(), (fd, audioData) =>
    //     pipe(
    //       audioData.audio,
    //       O.map(audio => fd.append(String(audioData.id), audio, 'rec.webm')),
    //       O.fold(
    //         () => fd,
    //         () => fd
    //       )
    //     )
    //   ),
    //   TE.tryCatchK(
    //     fd =>
    //       fetch(`${config.wpApiBase}/indiaudio/recordings`, {
    //         method: 'POST',
    //         body: fd
    //       }).then(response => response.json()),
    //     E.toError
    //   ),
    //   TE.chainEitherKW(
    //     flow(domain.Api.IAUploadRecordingsResponse.Type.decode, E.mapLeft(flow(formatValidationErrors, RA.join(', '), E.toError)))
    //   ),
    //   TE.map(files =>
    //     pipe(
    //       files.files,
    //       RA.map(
    //         RR.mapWithIndex((id, file) =>
    //           setAudioData(
    //             audioDataById(id).modify(audioData => ({
    //               ...audioData,
    //               remoteUrl: O.some(file)
    //             }))
    //           )
    //         )
    //       )
    //     )
    //   )
    // )()

    pipe(
      TE.tryCatch(
        () =>
          fetch(`${window.wc_add_to_cart_params.wc_ajax_url.replace('%%endpoint%%', 'add_to_cart')}`, {
            method: 'POST',
            body: formData
          }),
        E.toError
      ),
      TE.chainFirstIOK(() => () => {
        // update cart fragments
        jQuery(document.body).trigger('wc_fragment_refresh')
      }),
      TE.chainFirstIOK(() => () => {
        // reset to initial state
        onResetClick()
      })
    )()
  }, [formData, onResetClick])

  const onSelectName = (fieldId: string) => (item: NameItem) => {
    pipe(
      selectedNames,
      selectedNameDataIdByFieldId(fieldId),
      O.map(id => setSelectedNames(flow(nameLens(String(id)).set(O.some(item.name)))))
    )
  }

  const onClearName = (fieldId: string) => () => {
    pipe(
      selectedNames,
      selectedNameDataIdByFieldId(fieldId),
      O.map(id => setSelectedNames(flow(nameLens(String(id)).set(O.none))))
    )
  }

  const onStarGreetingChange = (id: string) => (text: O.Option<string>) => {
    pipe(starGreeting, starGreetingsGreetingLens(id).set(text), setStarGreeting)
  }

  const onStarGreetingSelectToggle = (id: string) => () => {
    pipe(starGreeting, starGreetingsSelectedLens(id).modify(b.not), setStarGreeting)
  }

  const onAccordionSelect = React.useCallback((key: AccordionEventKey) => {
    // If clicking the active panel, close it
    pipe(
      key,
      O.fromNullable,
      O.map(a => [a].flat()),
      O.chain(RA.head),
      setCurrentRecorder
    )
  }, [])

  const onVideoPlayerPaused = React.useCallback(() => {
    pipe(
      videoPlayerRef?.current,
      O.fromNullable,
      O.map(player => player.seekTo(0))
    )
  }, [])

  const onModalHintHide = React.useCallback(() => {
    setShowModalHint(false)

    // remove possible hash from URL
    history.replaceState({}, document.title, window.location.pathname)
  }, [])

  return (
    <Container fluid="lg">
      <ModalHint title="Hinweis" show={showModalHint} onHide={onModalHintHide}>
        <p>
          <strong>Die Audio-Aufnahme wurde nicht gefunden</strong>
          <br />
          Möglicherweise wurde sie gelöscht oder ist abgelaufen.
        </p>
      </ModalHint>
      <Row>
        <Col lg={{ offset: 4, span: 7 }} className="ps-lg-5">
          <ResetButton onClick={onResetClick}>
            <FontAwesomeIcon icon={faRotateRight} />
          </ResetButton>
          {pipe(
            O.fromNullable(title),
            O.foldMap(react.monoid)(title => <Title>{title}</Title>)
          )}
        </Col>
      </Row>
      {pipe(
        previewData,
        RD.fold(
          () => (
            <Row>
              <Col lg={4} className="d-flex flex-column align-items-center">
                <div className="px-lg-5">
                  <Wrapper>
                    <ImageGallery items={gallery} showThumbnails={false} showPlayButton={false} />
                  </Wrapper>
                </div>
              </Col>
              <Col lg={8} xl={7} className="d-flex flex-column align-items-center gap-3 ps-md-5 mt-5 mb-5">
                {pipe(
                  O.fromNullable(description),
                  O.foldMap(react.monoid)(description => <Description dangerouslySetInnerHTML={{ __html: description }} />)
                )}
                {pipe(
                  availableNames,
                  RD.foldMap(react.monoid)(names => (
                    <>
                      {pipe(
                        meta.fields,
                        RA.mapWithIndex((index, field) =>
                          pipe(
                            field,
                            domain.Meta.Fields.Field.ADT.match(
                              {
                                'pers-name': field => (
                                  <NameSelector
                                    title={field.label}
                                    description={field.description}
                                    names={names}
                                    index={index}
                                    key={field.id}
                                    onSelectName={onSelectName(field.id)}
                                    onClearName={onClearName(field.id)}
                                  />
                                ),
                                'pers-name-2': field => (
                                  <NameSelector
                                    title={field.label}
                                    description={field.description}
                                    names={names}
                                    index={index}
                                    key={field.id}
                                    onSelectName={onSelectName(field.id)}
                                    onClearName={onClearName(field.id)}
                                  />
                                )
                              },
                              () => <></>
                            )
                          )
                        )
                      )}
                    </>
                  ))
                )}
                {pipe(
                  starGreeting,
                  RR.foldMapWithIndex(s.Ord)(react.monoid)((key, data) => (
                    <StarGreeting
                      id={key}
                      title={data.title}
                      selected={data.selected}
                      onTextChange={onStarGreetingChange(key)}
                      onToggle={onStarGreetingSelectToggle(String(data.id))}
                      description={data.description}
                    />
                  ))
                )}

                {pipe(
                  audioData,
                  RR.foldMapWithIndex(s.Ord)(react.monoid)((key, data) => (
                    <Accordion
                      key={key}
                      activeKey={pipe(
                        currentRecorder,
                        O.getOrElse(() => '')
                      )}
                      onSelect={onAccordionSelect}>
                      <AudioRecorderWrapper>
                        <TextContainer>
                          {pipe(
                            data.description,
                            t.NonEmptyString.decode,
                            E.getOrElse(() => data.title)
                          )}
                          {!data.optional && <FontAwesomeIcon icon={faStar} />}
                        </TextContainer>
                        <ButtonContainer>
                          {pipe(
                            data.audio,
                            O.fold(
                              () => (
                                <AudioRecorder.View
                                  eventKey={key}
                                  disabled={pipe(
                                    currentRecorder,
                                    O.match(constFalse, _key => b.not(s.Eq.equals(_key, key)))
                                  )}
                                  onIsRecording={onIsRecording(key)}
                                  onRecordingComplete={onRecordingComplete(String(data.id))}
                                  maxRecordingTime={data.maxDuration}>
                                  {pipe(
                                    data.video,
                                    O.fold(
                                      () => faCommentText,
                                      () => faVideo
                                    ),
                                    icon => (
                                      <AccordionToggleButton eventKey={key} iconActive={icon} iconInactive={icon} />
                                    )
                                  )}
                                </AudioRecorder.View>
                              ),
                              () => react.monoid.empty
                            )
                          )}
                          {pipe(
                            data.url,
                            O.foldMap(react.monoid)(url => <AudioPlayer src={url} onDiscard={() => onDiscard(String(data.id))} />)
                          )}
                        </ButtonContainer>
                      </AudioRecorderWrapper>
                      <AccordionCollapse key={key} eventKey={key}>
                        {pipe(
                          data.video,
                          O.fold(
                            () =>
                              pipe(
                                options,
                                RD.match3(
                                  () => <Loading.Small />,
                                  () => <>Textvorschläge konnten nicht geladen werden</>,
                                  options =>
                                    pipe(
                                      options.proposals,
                                      RA.map(({ text }) => text),
                                      proposals => <ProposalSwiper proposals={proposals} />
                                    )
                                )
                              ),
                            video => (
                              <ReactPlayer
                                width="100%"
                                height="100%"
                                url={video.href}
                                ref={videoPlayerRef}
                                onPause={onVideoPlayerPaused}
                                playing={pipe(
                                  currentRecorder,
                                  O.match(constFalse, _key => s.Eq.equals(_key, key))
                                )}
                              />
                            )
                          )
                        )}
                      </AccordionCollapse>
                    </Accordion>
                  ))
                )}
                {pipe(
                  options,
                  RD.fold3(
                    () => <></>,
                    () => <></>,
                    options =>
                      pipe(
                        options?.buttonLabel,
                        O.fromNullable,
                        O.chainEitherK(label => t.NonEmptyString.decode(label.btn_preview)),
                        O.getOrElse(() => 'Vorschau erstellen'),
                        label => (
                          <PreviewButton size="lg" disabled={b.not(isOrderable)} onClick={onPreviewClick()}>
                            {label}
                            <FontAwesomeIcon icon={faRocket} className="ms-3" />
                          </PreviewButton>
                        )
                      )
                  )
                )}
              </Col>
            </Row>
          ),
          () => (
            <Loading>
              <h3>Vorschau wird erstellt</h3>
            </Loading>
          ),
          () => react.monoid.empty,
          ({ preview_url, transientId }) => (
            <Row>
              <Col md={4} className="d-flex flex-column align-items-center">
                <div className="px-md-5">
                  <Wrapper>
                    <Image src={image} />
                    <Overlay />
                    <AudioPlayer.Simple src={preview_url} />
                  </Wrapper>
                </div>
                <DownloadButton
                  target="_blank"
                  href={`${config.wpApiBase}/indiaudio/download/?url=${encodeURIComponent(preview_url)}`}
                  download={`${name}.mp3`}>
                  Download
                  <FontAwesomeIcon icon={faDownload} className="ms-3" />
                </DownloadButton>
              </Col>
              <Col md={7} className="d-flex flex-column align-items-start justify-content-center gap-3 ps-md-5 mt-5 mt-md-0">
                {pipe(
                  O.fromNullable(description),
                  O.foldMap(react.monoid)(description => <Description dangerouslySetInnerHTML={{ __html: description }} />)
                )}
                <ShareWidget url={`${config.homeUrl}#${transientId}`} imageUrl={image} />
                <OrderButton size="lg" disabled={b.not(isOrderable)} onClick={onOrderClick}>
                  Jetzt bestellen
                  <FontAwesomeIcon icon={faShoppingCart} className="ms-3" />
                </OrderButton>
              </Col>
            </Row>
          )
        )
      )}
    </Container>
  )
}
